import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ToastModule } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { AccordionModule } from 'primeng/accordion';
import { CheckboxModule } from 'primeng/checkbox';
import { Editor } from 'primeng/editor';

import { MessageService } from 'primeng/api';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { ProductService, CreateProductRequest, UpdateProductRequest } from '../../../core/services/product.service';
import { TerritoryService, GroupedTerritories, TerritoryItem } from '../../../core/services/territory.service';
import { AuthService } from '../../../core/services/auth';
import { UserService } from '../../../core/services/user.service';
import { LoadingSpinnerComponent } from '../../../shared/components/loading-spinner/loading-spinner.component';

@Component({
  selector: 'app-add-edit-product',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    ToggleSwitchModule,
    ToastModule,
    CardModule,
    AccordionModule,
    CheckboxModule,
    Editor,
    LoadingSpinnerComponent
  ],
  providers: [MessageService],
  templateUrl: './add-edit-product.html'
})
export class AddEditProduct implements OnInit {
  productForm: FormGroup;
  isEditMode = false;
  productId: string | null = null;
  loading = false;
  loadingProduct = false;
  loadingTerritories = false;
  
  // Territories data
  groupedTerritories: GroupedTerritories = {};
  selectedTerritories: string[] = [];

  // Currency options
  currencyOptions = [
    { label: 'EUR', value: 'EUR' },
    { label: 'USD', value: 'USD' },
    { label: 'GBP', value: 'GBP' },
    { label: 'CAD', value: 'CAD' },
    { label: 'AUD', value: 'AUD' },
    { label: 'JPY', value: 'JPY' },
    { label: 'CHF', value: 'CHF' },
    { label: 'CNY', value: 'CNY' },
    { label: 'INR', value: 'INR' }
  ];

  constructor(
    private fb: FormBuilder,
    private productService: ProductService,
    private territoryService: TerritoryService,
    private authService: AuthService,
    private userService: UserService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.productForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
      code: ['', [Validators.minLength(2), Validators.maxLength(20), Validators.pattern(/^[A-Z0-9_-]*$/)]], // Removed required validator
      description: ['', [Validators.required]],
      minPrice: [null, [Validators.min(0)]],
      maxPrice: [null, [Validators.min(0)]],
      price: ['', [Validators.maxLength(50)]],
      currency: ['EUR', [Validators.required]],
      availableTerritories: [[]],
      isActive: [true]
    });
  }

  ngOnInit() {
    this.loadTerritories();
    // Use reactive query params to ensure they're available, but only take the first emission
    // This ensures query params are properly loaded even if there's a timing issue
    this.route.queryParamMap.pipe(take(1)).subscribe(params => {
      const id = params.get('id');
      const mode = params.get('mode');
      this.checkMode(id, mode);
    });
  }

  loadTerritories() {
    this.loadingTerritories = true;
    this.territoryService.getTerritoriesGroupedByRegion().subscribe({
      next: (territories) => {
        this.groupedTerritories = territories;
        this.loadingTerritories = false;
        
        // If in add mode, select all territories by default
        if (!this.isEditMode) {
          this.selectAllTerritories();
        }
        
        console.log('Territories loaded:', territories);
        console.log('Current selectedTerritories:', this.selectedTerritories);
      },
      error: (error) => {
        console.error('Error loading territories:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load territories'
        });
        this.loadingTerritories = false;
      }
    });
  }

  checkMode(id: string | null, mode: string | null) {
    // Prevent multiple calls with the same parameters
    if (id && mode === 'edit' && this.productId === id && this.isEditMode) {
      return; // Already in edit mode for this product
    }
    
    if (id && mode === 'edit') {
      this.isEditMode = true;
      this.productId = id;
      this.loadProduct(id);
      // In edit mode, code is required
      this.productForm.get('code')?.setValidators([Validators.required, Validators.minLength(2), Validators.maxLength(25), Validators.pattern(/^[A-Z0-9_-|]+$/)]);
    } else {
      this.isEditMode = false;
      this.productId = null;
      // In add mode, code is optional (will be auto-generated if not provided)
      this.productForm.get('code')?.setValidators([Validators.minLength(2), Validators.maxLength(25), Validators.pattern(/^[A-Z0-9_-|]*$/)]);
    }
    this.productForm.get('code')?.updateValueAndValidity();
  }

  loadProduct(id: string) {
    this.loadingProduct = true;
    
    this.productService.getProductById(id).subscribe({
      next: (product) => {
        this.productForm.patchValue({
          name: product.name,
          code: product.code,
          description: product.description || '',
          minPrice: product.minPrice || null,
          maxPrice: product.maxPrice || null,
          price: product.price || '',
          currency: product.currency || 'EUR',
          availableTerritories: product.availableTerritories?.map(t => t._id) || [],
          isActive: product.isActive
        });
        
        // Set selected territories
        this.selectedTerritories = product.availableTerritories?.map(t => t._id) || [];
        
        console.log('Selected territory IDs:', this.selectedTerritories);
        
        this.loadingProduct = false;
      },
      error: (error) => {
        console.error('Error loading product:', error);
        this.loadingProduct = false;
        
        // Only redirect on specific errors (404, 403), not on all errors
        const errorStatus = error?.status;
        const errorMessage = error?.error?.message || error?.message || 'Failed to load product details';
        
        if (errorStatus === 404) {
          this.messageService.add({
            severity: 'error',
            summary: 'Product Not Found',
            detail: 'The product you are trying to edit does not exist.'
          });
          // Redirect only if product doesn't exist
          setTimeout(() => {
            this.router.navigate(['/admin/product-management']);
          }, 2000);
        } else if (errorStatus === 403) {
          this.messageService.add({
            severity: 'error',
            summary: 'Access Denied',
            detail: errorMessage
          });
          // Redirect only if access is denied
          setTimeout(() => {
            this.router.navigate(['/admin/product-management']);
          }, 2000);
        } else {
          // For other errors, show message but don't redirect immediately
          // Allow user to retry or manually navigate back
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
            life: 5000
          });
        }
      }
    });
  }

  selectAllTerritories() {
    // Get all territory IDs from all regions
    const allTerritoryIds: string[] = [];
    Object.values(this.groupedTerritories).forEach(territories => {
      territories.forEach(territory => {
        allTerritoryIds.push(territory._id);
      });
    });
    
    this.selectedTerritories = allTerritoryIds;
    
    // Update form control
    this.productForm.patchValue({
      availableTerritories: this.selectedTerritories
    });
    
    console.log('All territories selected:', this.selectedTerritories);
  }

  onTerritoryChange(territory: any, checked: boolean) {
    if (checked) {
      if (!this.selectedTerritories.includes(territory._id)) {
        this.selectedTerritories.push(territory._id);
      }
    } else {
      this.selectedTerritories = this.selectedTerritories.filter(id => id !== territory._id);
    }
    
    // Update form control
    this.productForm.patchValue({
      availableTerritories: this.selectedTerritories
    });
    
    console.log('Territory change:', territory.name, checked);
    console.log('Selected territories:', this.selectedTerritories);
  }

  isTerritorySelected(territory: any): boolean {
    return this.selectedTerritories.includes(territory._id);
  }

  getTerritoryCheckedState(territory: any): boolean {
    return this.selectedTerritories.includes(territory._id);
  }

  onTerritoryCheckboxChange(territory: any, event: any) {
    const checked = event.target.checked;
    this.onTerritoryChange(territory, checked);
  }

  onSubmit() {
    if (this.productForm.valid) {
      this.loading = true;
      const formValue = this.productForm.value;

      if (this.isEditMode && this.productId) {
        // Update existing product
        const updateRequest: UpdateProductRequest = {
          name: formValue.name,
          code: formValue.code,
          description: formValue.description,
          minPrice: formValue.minPrice,
          maxPrice: formValue.maxPrice,
          price: formValue.price,
          currency: formValue.currency,
          availableTerritories: this.selectedTerritories,
          isActive: formValue.isActive
        };

        this.productService.updateProduct(this.productId, updateRequest).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Product updated successfully'
            });
            this.loading = false;
            this.router.navigate(['/admin/product-management']);
          },
          error: (error) => {
            console.error('Error updating product:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error.error?.message || 'Failed to update product'
            });
            this.loading = false;
          }
        });
      } else {
        // Create new product
        const createRequest: CreateProductRequest = {
          name: formValue.name,
          code: formValue.code,
          description: formValue.description,
          minPrice: formValue.minPrice,
          maxPrice: formValue.maxPrice,
          price: formValue.price,
          currency: formValue.currency,
          availableTerritories: this.selectedTerritories
        };

        this.productService.createProduct(createRequest).subscribe({
          next: (createdProduct) => {
            // Automatically assign product to all active FSR users of the selected subscriber
            // Get subscriber ID from the created product (passed via interceptor)
            const subscriberId = createdProduct.subscriberId?._id || (typeof createdProduct.subscriberId === 'string' ? createdProduct.subscriberId : null);
            if (subscriberId) {
              this.assignProductToFSRUsers(createdProduct._id, subscriberId);
            }
            
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Product created successfully'
            });
            this.loading = false;
            this.router.navigate(['/admin/product-management']);
          },
          error: (error) => {
            console.error('Error creating product:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: error.error?.message || 'Failed to create product'
            });
            this.loading = false;
          }
        });
      }
    }
  }

  onCancel() {
    this.router.navigate(['/admin/product-management']);
  }

  getPageTitle(): string {
    return this.isEditMode ? 'Edit Product' : 'Add New Product';
  }

  getSubmitButtonText(): string {
    return this.isEditMode ? 'Update Product' : 'Save Product';
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.productForm.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  getFieldError(fieldName: string): string {
    const field = this.productForm.get(fieldName);
    if (field && field.errors) {
      const fieldLabels: { [key: string]: string } = {
        name: 'Product Name',
        code: 'Product Code',
        description: 'Description',
        minPrice: 'Minimum Price',
        maxPrice: 'Maximum Price',
        price: 'Price',
        currency: 'Currency',
        availableTerritories: 'Available Territories',
        isActive: 'Active Status'
      };
      
      const fieldLabel = fieldLabels[fieldName] || fieldName;
      
      if (field.errors['required']) return `${fieldLabel} is required`;
      if (field.errors['minlength']) return `${fieldLabel} must be at least ${field.errors['minlength'].requiredLength} characters`;
      if (field.errors['maxlength'] && fieldName !== 'description') return `${fieldLabel} must not exceed ${field.errors['maxlength'].requiredLength} characters`;
      if (field.errors['min']) return `${fieldLabel} must be at least ${field.errors['min'].min}`;
      if (field.errors['pattern']) return `${fieldLabel} format is invalid`;
    }
    return '';
  }

  getRegionKeys(): string[] {
    return Object.keys(this.groupedTerritories);
  }

  getTerritoriesByRegion(region: string): TerritoryItem[] {
    return this.groupedTerritories[region] || [];
  }

  getExpandedPanels(): number[] {
    // Return array of all panel indices to expand all territories by default
    return this.getRegionKeys().map((_, index) => index);
  }

  getEditorConfig() {
    return {
      style: {
        'height': '200px',
        'font-size': '14px',
        'border': '1px solid #d1d5db',
        'border-radius': '6px'
      },
      formats: ['bold', 'italic', 'underline', 'list', 'bullet', 'link', 'header', 'font', 'size'],
      modules: {
        toolbar: {
          container: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}],
            ['link']
          ]
        }
      }
    };
  }

  /**
   * Assigns a newly created product to all active FSR users of the selected subscriber
   * @param productId The ID of the newly created product
   * @param subscriberId The subscriber ID (passed via interceptor and returned in product response)
   */
  private assignProductToFSRUsers(productId: string, subscriberId: string): void {
    try {
      if (!subscriberId) {
        console.warn('Cannot assign product to FSR users: No subscriber ID found');
        return;
      }

      console.log('Assigning product to FSR users for subscriber:', subscriberId);

      // Get all users for this subscriber
      this.userService.getUsersBySubscriberId(subscriberId).subscribe({
        next: (users) => {
          // Filter for active FSR users
          const fsrUsers = users.filter(user => 
            user.roleId?.identification === 'FSR' && 
            user.isActive && 
            user.status === 'APPROVED'
          );

          if (fsrUsers.length === 0) {
            console.log('No active FSR users found for this subscriber');
            return;
          }

          console.log(`Found ${fsrUsers.length} active FSR users to assign product to`);

          // Update each FSR user to add the new product to their products array
          // Fetch each user individually to ensure we have the latest products data
          const updateObservables = fsrUsers.map(user => {
            return this.userService.getUserById(user._id).pipe(
              switchMap((fullUser) => {
                const currentProducts = fullUser.products?.map((p: any) => 
                  typeof p === 'string' ? p : p._id
                ) || [];
                
                // Only add product if not already assigned
                if (!currentProducts.includes(productId)) {
                  const updatedProducts = [...currentProducts, productId];
                  
                  return this.userService.updateUser(fullUser._id, {
                    firstName: fullUser.firstName,
                    lastName: fullUser.lastName,
                    email: fullUser.email,
                    roleId: fullUser.roleId?._id || '',
                    products: updatedProducts
                  } as any);
                }
                
                // Return a successful observable if product already assigned
                return of(null);
              })
            );
          });

          if (updateObservables.length > 0) {
            forkJoin(updateObservables).subscribe({
              next: () => {
                const assignedCount = fsrUsers.length;
                console.log(`Successfully processed product assignment for ${assignedCount} FSR users`);
                this.messageService.add({
                  severity: 'info',
                  summary: 'Product Assignment',
                  detail: `Product automatically assigned to active FSR user(s) of the selected subscriber`
                });
              },
              error: (error) => {
                console.error('Error assigning product to FSR users:', error);
                // Don't show error to user as product was created successfully
              }
            });
          } else {
            console.log('All FSR users already have this product assigned');
          }
        },
        error: (error) => {
          console.error('Error fetching FSR users for product assignment:', error);
          // Don't show error to user as product was created successfully
        }
      });
    } catch (error) {
      console.error('Error in assignProductToFSRUsers:', error);
      // Don't show error to user as product was created successfully
    }
  }
}
